import { z } from "zod";
import { copilotChannelIdSchema } from "./messaging.js";
// The Staff Copilot contract (CHT-733; docs/architecture/milestone-c-contracts.md section 4.6; ADR 0012). FROZEN at the Wave 0
// commit. Lane E builds it.
//
// The copilot is private: it answers ONE staff member about ONE conversation that this staff member may read. It has no AI
// response lease, no write tool and no send tool. Its messages live only in a `staff_copilot` channel whose members are that
// staff member and the copilot user. A draft becomes a customer message only when staff copy it into the normal concierge
// composer and press send there.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
/** The agent id of the copilot in `agent_runs.agent_id` and in traces. Never `customer-concierge`. */
export const STAFF_COPILOT_AGENT_ID = "staff-copilot";
/** The read-only tools of the copilot (CHT-733 plan item 4). A copilot registry with any other tool is a defect. */
export const staffCopilotToolNames = [
    "getConversationContext",
    "getCustomerOperationalSummary",
    "getOpenRequests",
    "getPayments",
    "searchCatalog",
    "getMemoryFacts",
    "getRecommendationProvenance",
];
/** The preset questions of the third pane. `FREE_FORM` uses `question`. */
export const staffCopilotPresetValues = [
    "SUMMARY",
    "OPEN_WORK",
    "DRAFT_REPLY",
    "EXPLAIN_RECOMMENDATION",
    "FREE_FORM",
];
/** Where a statement of an answer comes from (CHT-733 plan item 7). Inference is always labelled. */
export const staffCopilotSourceValues = [
    "CUSTOMER_STATED",
    "MEMORY",
    "DOMAIN_STATE",
    "CATALOG",
    "EXTERNAL_LIVE",
    "INFERENCE",
];
export const STAFF_COPILOT_LIMITS = Object.freeze({
    questionMaxChars: 2_000,
    draftMaxChars: 4_000,
});
/** `POST /staff/conversations/:conversationId/copilot/runs`. Strict. Needs an `Idempotency-Key`. Answers an SSE stream. */
export const staffCopilotRunBodySchema = z
    .strictObject({
    preset: z.enum(staffCopilotPresetValues),
    question: z.string().trim().min(1).max(STAFF_COPILOT_LIMITS.questionMaxChars).nullable(),
})
    .refine((value) => (value.preset === "FREE_FORM") === (value.question !== null), {
    message: "question is required for FREE_FORM and forbidden otherwise",
});
/** `GET /staff/conversations/:conversationId/copilot/session`: the private channel of the caller for this conversation. */
export const staffCopilotSessionSchema = z.strictObject({
    conversationId: uuid,
    channel: z.strictObject({ type: z.literal("staff_copilot"), id: copilotChannelIdSchema }),
});
/**
 * The data of the staff-only part `data-lafayette-staff-copilot-meta` (version 1), the last data part of a copilot stream before
 * the run-status part. It lists the sources the answer used and, for `DRAFT_REPLY`, the draft text. It never reaches a customer
 * client: the customer UI part support does not declare it and the customer routes never build it.
 */
export const staffCopilotMetaPartV1Schema = z.strictObject({
    schemaVersion: z.literal(1),
    runId: uuid,
    sources: z.array(z.enum(staffCopilotSourceValues)).max(staffCopilotSourceValues.length),
    draft: z
        .strictObject({ text: z.string().min(1).max(STAFF_COPILOT_LIMITS.draftMaxChars) })
        .nullable(),
});
export const STAFF_COPILOT_META_PART_TYPE = "data-lafayette-staff-copilot-meta";
